import React, { useRef, useState, memo } from 'react';
import PropTypes from 'prop-types';
import ReactMonacoEditor from '@monaco-editor/react';
import setClass from '../../common/set-class';
import Icon from '../icon';
const defaultOptions = {
    automaticLayout: true,
    scrollBeyondLastLine: false,
    wordWrap: 'on',
    minimap: { enabled: false },
    scrollbar: {
        useShadows: false,
        verticalScrollbarSize: 6,
    },
    lineDecorationsWidth: 5,
    lineNumbersMinChars: 2,
    fontSize: 14,
};
const ScriptEditor = ({ editorDidMount, value, options, placeholder, error, isDisabled, className, ...props }) => {
    const [isFocused, setIsFocused] = useState(false);
    const monacoRef = useRef();
    const handleEditorWillMount = (monaco) => {
        monaco.editor?.defineTheme('bumblebeeTheme', {
            base: 'vs',
            inherit: true,
            colors: { 'editor.foreground': '#000000' },
            rules: [
                { token: 'keyword', foreground: '0061fe' },
                { token: 'string', foreground: '44ae41' },
                // next colors I get using chrome dev tools color-picker here
                // https://www.figma.com/file/VgUvvxhB39h2KjgdKlrnid/Custom-Scripts-Library-Item-1.5?node-id=401%3A5008
                // there are no such colors in colors.json file
                { token: 'number', foreground: 'e54475' },
                { token: 'operator', foreground: 'f88a04' },
            ],
        });
    };
    const handleEditorDidMount = (editor, editorInstance) => {
        if (editorDidMount) {
            editorDidMount(editor, editorInstance);
        }
        monacoRef.current = editor;
        monacoRef.current.onDidBlurEditorWidget(() => setIsFocused(false));
        monacoRef.current.onDidFocusEditorWidget(() => setIsFocused(true));
    };
    const onPlaceholderClick = () => {
        if (!monacoRef.current) {
            return;
        }
        monacoRef.current.layout();
        monacoRef.current.focus();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: setClass([
                'monaco--wrapper b-up-container',
                className,
                options?.readOnly && 'monaco--wrapper__readonly',
                error && 'b-up-container__error',
            ]) },
            React.createElement(ReactMonacoEditor, { height: "90vh", beforeMount: handleEditorWillMount, onMount: handleEditorDidMount, value: value, theme: "bumblebeeTheme", options: {
                    ...defaultOptions,
                    ...options,
                }, ...props }),
            !isFocused && !value && (React.createElement("div", { onClick: onPlaceholderClick, className: "monaco--placeholder" }, placeholder)),
            isDisabled && (React.createElement("div", { style: {
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    top: 0,
                    right: 0,
                    backgroundColor: 'var(--color-neutral-0)',
                    opacity: 0.6,
                } }))),
        error && (React.createElement("div", { className: "b-date-picker__err-msg" },
            React.createElement(Icon, { name: "triangle-exclamation", className: "b-txt-input__err-icon" }),
            React.createElement("p", { className: "b-txt b-txt--error" },
                " ",
                error,
                " ")))));
};
export default memo(ScriptEditor);
ScriptEditor.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    editorDidMount: PropTypes.func,
    placeholder: PropTypes.string,
    options: PropTypes.object,
    language: PropTypes.string,
    error: PropTypes.string,
    height: PropTypes.number,
    isDisabled: PropTypes.bool,
    className: PropTypes.string,
};
ScriptEditor.defaultProps = {
    value: undefined,
    onChange: undefined,
    editorDidMount: undefined,
    placeholder: 'Your script here',
    options: {},
    language: 'shell',
    error: undefined,
    height: 300,
    isDisabled: false,
    className: '',
};
